import { css, cx } from "styled-system/css";
import DOMPurify from "dompurify";

interface HtmlContentProps {
  html: string;
  className?: string;
}

/** Typography + nested-element rules shared with read-only TipTap HTML (use for plain text that should match). */
export const htmlContentProseClassName = css({
  color: "text.dark",
  fontFamily: "body",
  fontSize: "md",
  lineHeight: "sm",
  "& a": {
    color: "text.accent.blue.dark",
    cursor: "pointer",
  },
  "& ul": {
    listStyleType: "none",
    pl: "0",
  },
  "& ul li": {
    position: "relative",
    pl: "1.2em",
    _before: {
      content: "'•'",
      position: "absolute",
      left: "0",
      color: "text.dark",
    },
  },
  "& blockquote": {
    borderLeft: "2px solid",
    borderLeftColor: "border.light",
    pl: "400",
    ml: "0",
    mr: "0",
    my: "300",
    fontStyle: "italic",
    py: "250",
    borderRadius: "0 150 150 0",
  },
  "& em, .lexical-editor-text-italic": {
    fontStyle: "italic",
  },
  "& u, .lexical-editor-text-underline": {
    textDecoration: "underline",
  },
  "& strong, .lexical-editor-text-bold": {
    fontWeight: "semiBold",
  },
});

/**
 * Component for rendering HTML content generated by TipTap editor.
 * Uses consistent styling with the TextEditor component to ensure
 * HTML content appears the same whether in edit or read-only mode.
 */
export const HtmlContent = ({ html, className }: HtmlContentProps) => {
  // Sanitize HTML to prevent XSS attacks
  const sanitizedHtml = DOMPurify.sanitize(html, {
    // Allow only the HTML tags that are allowed in the TextEditor component
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "a",
      "ul",
      "ol",
      "li",
      "blockquote",
    ],
    // Allow safe attributes
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
    // Remove all unsafe protocols
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\w-]+(?:[^a-z+.\w-:]|$))/i,
  });

  const contentClassName = cx(htmlContentProseClassName, className);

  return (
    <div
      className={contentClassName}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};
